/* AdminMarket.jsx — Market Trends Dashboard
   Route: /admin/market
   Toont marktdata uit quote_market_data: KPIs, prijs-per-kWp over tijd, top 10 merken.
*/
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, TrendingUp, BarChart3, Package, Zap, Activity } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = "#003049";
const YELLOW = "#FCBF49";
const GREEN = "#1F7A4B";

export default function AdminMarket() {
  const nav = useNavigate();
  const [token] = useState(localStorage.getItem("dz_admin_token"));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) { nav("/admin"); return; }
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/market-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem("dz_admin_token");
          nav("/admin");
        } else {
          toast.error("Kon marktdata niet laden");
        }
      } finally { setLoading(false); }
    };
    load();
  }, [token, nav]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F7FA]">
        <Loader2 className="w-8 h-8 text-[#FCBF49] animate-spin" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const panel = data?.panel_brands_top10 || [];
  const inverter = data?.inverter_brands_top10 || [];
  const monthly = data?.monthly_avg_price || [];

  const empty = (summary.count || 0) === 0;

  return (
    <div className="min-h-screen bg-[#F4F7FA] text-[#003049] font-sans">
      <header className="bg-white border-b border-[#E5E9EE] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" data-testid="admin-market-back"
              className="inline-flex items-center gap-1.5 text-sm text-[#52606E] hover:text-[#003049]">
              <ArrowLeft className="w-4 h-4" /> Terug naar admin
            </Link>
            <span className="text-[#E5E9EE]">·</span>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#FCBF49]" /> Market Trends
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-10 space-y-8">
        {empty ? (
          <EmptyState />
        ) : (
          <>
            <KpiGrid summary={summary} />
            <PriceTrendChart data={monthly} />
            <div className="grid lg:grid-cols-2 gap-6">
              <BrandsBar title="Top 10 paneelmerken" icon={Package} data={panel} testid="market-panel-brands" />
              <BrandsBar title="Top 10 invertermerken" icon={Zap} data={inverter} testid="market-inverter-brands" />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-2xl border border-[#EFF2F5] p-12 text-center" data-testid="market-empty">
      <Activity className="w-12 h-12 text-[#FCBF49] mx-auto mb-4" />
      <h2 className="text-2xl font-bold mb-2">Nog geen marktdata</h2>
      <p className="text-[#52606E] max-w-md mx-auto">
        Zodra gebruikers offertes uploaden via <Link to="/offertes-vergelijken" className="underline">/offertes-vergelijken</Link>,
        wordt hier de geanonimiseerde markt-data getoond: gemiddelde prijs per kWp over tijd
        en de populairste merken voor panelen en inverters.
      </p>
    </div>
  );
}

function KpiGrid({ summary }) {
  const fmt = (n, d = 0) => Number(n || 0).toLocaleString("nl-NL", { maximumFractionDigits: d });
  const kpis = [
    { label: "Totaal aantal offertes", value: fmt(summary.count), suffix: "", testid: "kpi-count" },
    { label: "Gemiddelde €/kWp", value: `€ ${fmt(summary.avg_price_per_kwp)}`, suffix: "", testid: "kpi-avg-price" },
    { label: "Goedkoopste €/kWp", value: `€ ${fmt(summary.min_price_per_kwp)}`, color: GREEN, testid: "kpi-min-price" },
    { label: "Duurste €/kWp", value: `€ ${fmt(summary.max_price_per_kwp)}`, color: "#D62828", testid: "kpi-max-price" },
    { label: "Gemiddelde systeemgrootte", value: `${fmt(summary.avg_system_kwp, 1)} kWp`, testid: "kpi-avg-kwp" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {kpis.map((k, i) => (
        <div key={i} data-testid={k.testid} className="bg-white rounded-2xl border border-[#EFF2F5] p-5">
          <div className="text-xs uppercase tracking-wider text-[#52606E] font-semibold mb-2">{k.label}</div>
          <div className="text-2xl font-bold leading-tight" style={{ color: k.color || NAVY }}>{k.value}</div>
        </div>
      ))}
    </div>
  );
}

function PriceTrendChart({ data }) {
  if (!data?.length) {
    return (
      <div className="bg-white rounded-2xl border border-[#EFF2F5] p-6 text-sm text-[#52606E]">
        Nog onvoldoende data om een trend over tijd te tonen.
      </div>
    );
  }
  return (
    <section className="bg-white rounded-2xl border border-[#EFF2F5] p-6" data-testid="market-price-trend">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="w-5 h-5 text-[#FCBF49]" />
        <div>
          <h2 className="text-xl font-bold">Prijs per kWp over tijd</h2>
          <p className="text-sm text-[#52606E]">Gemiddelde €/kWp uit alle vergeleken offertes, per maand.</p>
        </div>
      </div>
      <div className="h-72 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E9EE" />
            <XAxis dataKey="month" stroke="#52606E" fontSize={11} />
            <YAxis stroke="#52606E" fontSize={11} tickFormatter={(v) => `€${v}`} />
            <Tooltip
              formatter={(v, n) => n === "avg_price_per_kwp" ? [`€ ${Number(v).toLocaleString("nl-NL")}`, "Gem. €/kWp"] : [v, "Aantal offertes"]}
              labelStyle={{ color: NAVY, fontWeight: 600 }}
            />
            <Legend />
            <Line type="monotone" dataKey="avg_price_per_kwp" stroke={NAVY} strokeWidth={2.5}
                  dot={{ fill: YELLOW, stroke: NAVY, strokeWidth: 2, r: 5 }} name="Gem. €/kWp" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function BrandsBar({ title, icon: Icon, data, testid }) {
  if (!data?.length) {
    return (
      <section className="bg-white rounded-2xl border border-[#EFF2F5] p-6 text-sm text-[#52606E]" data-testid={testid}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-5 h-5 text-[#FCBF49]" />
          <h2 className="text-xl font-bold text-[#003049]">{title}</h2>
        </div>
        Nog geen data.
      </section>
    );
  }
  return (
    <section className="bg-white rounded-2xl border border-[#EFF2F5] p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-[#FCBF49]" />
        <h2 className="text-xl font-bold">{title}</h2>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E9EE" horizontal={false} />
            <XAxis type="number" stroke="#52606E" fontSize={11} />
            <YAxis dataKey="brand" type="category" stroke="#52606E" fontSize={11} width={80} />
            <Tooltip formatter={(v) => [v, "Aantal offertes"]} labelStyle={{ color: NAVY, fontWeight: 600 }} />
            <Bar dataKey="count" fill={YELLOW} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
